// Where the writer left off, and the project figures the Library's home page and column show
// (Apps/Shared/Rooms/LibraryModel+Home.swift, LibraryHome.swift's ProjectRow): LeftOff, `placeName`, `partProgress`,
// and a project row's "kind · words" detail. Pure: the app keeps LeftOff in localStorage (`ws:leftOff-<project>`,
// per browser, never synced — opening isn't an edit) and passes it in.
import type { Project, ProjectType, TreeNode } from '../contract.ts';
import { journalSummary, parts, untouched, type JournalPost, type JournalSummary, type Part } from './libraryHome.ts';
import { JournalPosts } from '../edit/editProject.ts';
import { journalPostsInTree } from '../model/posts.ts';

/** Where the writer left off in a project, on this device: the document and the caret. */
export interface LeftOff {
  fragmentId: string;
  /** The caret, in Unicode scalars into the document's text. */
  offset: number;
  /** When it was recorded (ms since 1970). */
  at: number;
}

/**
 * A stored LeftOff, checked. Records from before W7 (no `at`) kept the caret in UTF-16: `utf16` says so, and the
 * caller converts with the fragment's text.
 */
export function readLeftOff(raw: unknown): (LeftOff & { utf16: boolean }) | null {
  if (!raw || typeof raw !== 'object') return null;
  const v = raw as Record<string, unknown>;
  if (typeof v.fragmentId !== 'string' || !v.fragmentId || !Number.isInteger(v.offset) || (v.offset as number) < 0) return null;
  const at = typeof v.at === 'number' && Number.isFinite(v.at) ? v.at : null;
  return { fragmentId: v.fragmentId, offset: v.offset as number, at: at ?? 0, utf16: at === null };
}

/** A LeftOff that still points at a document in the project (not trashed), as `LibraryModel.leftOff` reads it. */
export function validLeftOff<T extends { fragmentId: string }>(project: Project, leftOff: T | null): T | null {
  if (!leftOff) return null;
  const f = project.fragment(leftOff.fragmentId);
  return f && !f.trashed ? leftOff : null;
}

/** The node and its ancestors, innermost first; null when the fragment isn't in the tree. */
function ancestry(nodes: TreeNode[], fragmentId: string, trail: TreeNode[] = []): TreeNode[] | null {
  for (const n of nodes) {
    const here = [n, ...trail];
    if (n.fragmentId === fragmentId) return here;
    const inner = ancestry(n.children, fragmentId, here);
    if (inner) return inner;
  }
  return null;
}

/** Where a document sits, as the writer would say it: its chapter and its place among the chapter's scenes
 *  ("Chapter 14, Scene 3"; the chapter alone for its own text), an unnumbered chapter by its title ("Prologue, Scene 2"),
 *  else the document's own title. */
export function placeName(project: Project, fragmentId: string): string {
  const manuscript = project.manuscriptId();
  if (manuscript) {
    const path = ancestry(project.tree(manuscript), fragmentId);
    if (path) {
      const openers = project.openers();
      for (const node of path) {
        const opener = node.fragmentId ? openers.get(node.fragmentId) : undefined;
        if (opener?.kind !== 'chapter') continue;
        const name = opener.number === null ? opener.title : `Chapter ${opener.number}`;
        if (node.fragmentId === fragmentId) return name;
        const index = scenesUnder(node).findIndex((n) => n.fragmentId === fragmentId);
        return index < 0 ? name : `${name}, Scene ${index + 1}`;
      }
    }
  }
  return project.fragment(fragmentId)?.title ?? '';
}

/** A chapter's scenes in reading order: the documents under it at any depth (folders aren't scenes). */
function scenesUnder(chapter: TreeNode): TreeNode[] {
  return chapter.children.flatMap((n) => (n.isFolder || n.children.length > 0 ? scenesUnder(n) : [n]));
}

/** The manuscript's top level as the home page's bar: parts, or chapters when the book has no parts. */
export function partProgress(project: Project): Part[] {
  const manuscript = project.manuscriptId();
  if (!manuscript) return [];
  const top = project.tree(manuscript).filter((n) => !n.fragmentId || project.fragment(n.fragmentId)?.trashed !== true);
  return parts(
    top.map((n) => ({ title: n.title, words: project.subtreeWords(n) })),
    project.targets().manuscript,
  );
}

/** What the bar's items are called when there are too many to name (`LibraryModel.partsCount`): a screenplay's top-level
 *  folders are acts, a book's are parts when any holds folders of its own, else chapters. */
export function partsNoun(project: Project): 'acts' | 'parts' | 'chapters' {
  if (project.type === 'screenplay') return 'acts';
  const manuscript = project.manuscriptId();
  const top = manuscript ? project.tree(manuscript) : [];
  return top.some((n) => n.children.some((c) => c.isFolder)) ? 'parts' : 'chapters';
}

/** A journal's Home card (`LibraryModel.journalSummary`): its posts (`journalPosts`: not folders, nor sections) newest
 *  first as the binder keeps them, each with its status option and publish date. */
export function journalProgress(project: Project): JournalSummary {
  const manuscript = project.manuscriptId();
  const posts: JournalPost[] = [];
  for (const { fragmentId } of journalPostsInTree(manuscript ? project.tree(manuscript) : [])) {
    const f = project.fragment(fragmentId);
    if (!f || f.trashed) continue;
    const status = f.metadata['status'];
    const date = f.metadata['publish-date'];
    posts.push({
      id: f.id, title: f.title || 'Untitled', status: status?.type === 'text' ? status.value : null,
      date: date?.type === 'date' ? date.value : null,
    });
  }
  const field = project.allFields().find((f) => f.id === 'status');
  const statuses = field?.options?.map((o) => o.id) ?? [JournalPosts.idea, JournalPosts.draft, JournalPosts.scheduled, JournalPosts.published];
  return journalSummary(posts, statuses);
}

/** "6 published", "1 draft" (`LibraryModel.journalCount`): a status the writer renamed keeps its name ("Ready 2"). */
export function journalCount(project: Project, count: { status: string | null; count: number }, format: (n: number) => string = String): string {
  const n = count.count;
  const option = count.status ? project.allFields().find((f) => f.id === 'status')?.options?.find((o) => o.id === count.status) : undefined;
  const standard: Record<string, [string, string, string]> = {
    [JournalPosts.idea]: ['Idea', 'idea', 'ideas'], [JournalPosts.draft]: ['Draft', 'draft', 'drafts'],
    [JournalPosts.scheduled]: ['Scheduled', 'scheduled', 'scheduled'], [JournalPosts.published]: ['Published', 'published', 'published'],
  };
  const words = option ? standard[option.id] : undefined;
  if (!option || !words || option.name !== words[0]) return `${option?.name ?? 'No status'} ${format(n)}`;
  return `${format(n)} ${n === 1 ? words[1] : words[2]}`;
}

/** "Manuscript", "Screenplay", "Journal" (`ProjectType.localizedName`). */
export function projectKind(type: ProjectType | null | undefined): string {
  return type === 'screenplay' ? 'Screenplay' : type === 'journal' ? 'Journal' : 'Manuscript';
}

const RELATIVE = (locale: string) => new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

/** "edited yesterday", "edited 3 days ago" (`.relative(presentation: .named)`). */
export function editedAgo(date: Date, now: Date = new Date(), locale = 'en-GB'): string {
  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(seconds);
  const f = RELATIVE(locale);
  const [value, unit]: [number, Intl.RelativeTimeFormatUnit] =
    abs < 60 ? [seconds, 'second'] : abs < 3600 ? [Math.round(seconds / 60), 'minute'] : abs < 86_400 ? [Math.round(seconds / 3600), 'hour'] : [Math.round(seconds / 86_400), 'day'];
  return `edited ${f.format(value, unit)}`;
}

/**
 * A project row's second line (`ProjectRow.detail`): "Manuscript · untouched since June. It'll keep." after six weeks
 * alone, else "Manuscript · 12,480 words", else when it was edited.
 */
export function projectDetail(
  o: { type?: ProjectType | null; words?: number | null; modifiedAt?: Date | null },
  now: Date = new Date(),
  locale = 'en-GB',
): string {
  const kind = projectKind(o.type);
  const modified = o.modifiedAt && !Number.isNaN(o.modifiedAt.getTime()) ? o.modifiedAt : null;
  const resting = modified ? untouched(modified, now, locale) : null;
  if (resting) return `${kind} · ${resting}`;
  if (o.words && o.words > 0) return `${kind} · ${new Intl.NumberFormat(locale).format(o.words)} ${o.words === 1 ? 'word' : 'words'}`;
  return modified ? `${kind} · ${editedAgo(modified, now, locale)}` : kind;
}
